import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Add, CalendarMonth, CheckCircleOutline, Close, Edit, KeyboardArrowDown, MailOutline, PersonAddAlt, Refresh, RestartAlt, ZoomIn, ZoomOut } from "@mui/icons-material";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { WorkspaceInlineLoader } from "../../common/WorkspaceInlineLoader";
import {
  createWorkspaceDepartment,
  clockWorkspaceAttendance,
  getWorkspaceAttendance,
  updateWorkspaceAttendanceSettings,
  archiveWorkspaceRoleTemplate,
  cloneWorkspaceRoleTemplate,
  createWorkspacePerson,
  createWorkspaceRoleTemplate,
  createWorkspaceTeam,
  getWorkspacePeople,
  resendWorkspacePersonInvite,
  searchWorkspacePeopleUsers,
  updateWorkspaceDepartment,
  updateWorkspacePerson,
  updateWorkspaceRoleTemplate,
  updateWorkspaceTeam,
} from "../../../../services/workspaceApi";
import { HelpTrigger, ModuleTabStrip, SectionHeader } from "../../common/WorkspaceChrome";
import { peopleRows, relationshipModels } from "../../../../data/workspaceData";

const emptyPersonForm = {
  id: "",
  userId: "",
  name: "",
  email: "",
  employmentType: "Employee",
  department: "",
  team: "",
  designation: "",
  managerName: "",
  managerUser: "",
  leaveApprovers: [],
  workspaceRole: "Employee",
  accessLevel: "Member",
  moduleAccess: ["Home", "Work"],
  status: "Invited",
  joiningDate: "",
  phone: "",
  location: "",
  notes: "",
  projectRefs: [],
};

const fallbackMeta = {
  departments: [],
  teams: [],
  departmentRows: [],
  teamRows: [],
  roleTemplates: [
    { name: "Employee", accessLevel: "Member", moduleAccess: ["Home", "Schedule", "Work"], permissions: [] },
  ],
  employmentTypes: ["Employee", "Intern", "Freelancer", "Contractor", "Instructor", "Client", "Other"],
  accessLevels: ["Owner", "Admin", "Manager", "Member", "Viewer"],
  statuses: ["Active", "Invited", "On Leave", "Disabled", "Exited"],
  availableProjects: [],
  modules: [
    "Home",
    "Schedule",
    "Projects",
    "Work",
    "People",
    "CRM",
    "Training",
    "Knowledge",
    "Finance",
    "Marketing",
    "Analytics",
    "Administration",
  ].map((key) => ({ key, label: key })),
  selectedProject: "",
};

const PeopleDialog = ({ children, description, id, onClose, open, title }) => (
  <Dialog
    PaperProps={{ className: "people-mui-dialog" }}
    aria-labelledby={id}
    fullWidth
    maxWidth="md"
    onClose={(_, reason) => {
      if (reason !== "backdropClick") onClose();
    }}
    open={open}
  >
    <DialogTitle
      id={id}
      className="people-mui-dialog-title"
      sx={{ alignItems: "flex-start", display: "flex", gap: 2, justifyContent: "space-between" }}
    >
      <div className="people-mui-dialog-heading" style={{ alignItems: "flex-start", display: "flex", flexDirection: "column", gap: 4 }}>
        <strong style={{ color: "#17231f", display: "block", fontSize: 19, fontWeight: 600, lineHeight: 1.25 }}>{title}</strong>
        {description ? <p style={{ color: "rgba(76, 92, 86, 0.72)", display: "block", fontSize: 12, fontWeight: 400, lineHeight: 1.45, margin: 0 }}>{description}</p> : null}
      </div>
      <IconButton aria-label="Close dialog" onClick={onClose} size="small" sx={{ flex: "0 0 auto", marginTop: "1px" }}>
        <Close fontSize="small" />
      </IconButton>
    </DialogTitle>
    <DialogContent className="people-mui-dialog-content" dividers>{children}</DialogContent>
  </Dialog>
);

const modalLayerStyle = {
  position: "fixed",
  zIndex: 2147483647,
  inset: 0,
  display: "grid",
  placeItems: "center",
  overflowY: "auto",
  background: "rgba(17, 28, 25, 0.52)",
  padding: 24,
};
const ModalPortal = ({ children }) => createPortal(
  React.cloneElement(children, { style: { ...(children.props.style || {}), ...modalLayerStyle } }),
  document.body
);

const getPersonInitials = (name = "") => name
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0])
  .join("")
  .toUpperCase() || "U";

const getPersonAvatar = (person = {}) =>
  person.avatarUrl
  || person.avatar_url
  || person.linkedUser?.avatar
  || person.linkedUser?.avatarUrl
  || person.user?.avatar?.url
  || person.user?.avatarUrl
  || "";

const OrganizationTreeNode = ({ node, onSelect, depth = 0 }) => {
  const [expanded, setExpanded] = useState(depth < 2);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const reportsCount = node.children.length;
  const person = node.person;
  const avatar = getPersonAvatar(person);
  const teamLabel = person.department || person.team || "Workspace";

  useEffect(() => {
    setAvatarFailed(false);
  }, [avatar]);

  return (
    <li className={`org-tree-node ${depth === 0 ? "org-tree-root-node" : ""} ${reportsCount ? "has-reports" : ""} ${expanded ? "is-expanded" : "is-collapsed"}`}>
      <article className="org-person-card">
        <button aria-label={`Edit ${person.name}`} className="org-person-card-main" onClick={() => onSelect(person)} type="button">
          <span className="org-person-avatar" aria-hidden="true">
            {avatar && !avatarFailed ? <img alt="" onError={() => setAvatarFailed(true)} src={avatar} /> : getPersonInitials(person.name)}
          </span>
          <span className="org-person-card-copy">
            <small>{teamLabel}</small>
            <strong>{person.name}</strong>
            <span>{person.designation || person.workspaceRole || "Team member"}</span>
          </span>
        </button>
        {reportsCount ? (
          <button
            aria-expanded={expanded}
            aria-label={`${expanded ? "Collapse" : "Expand"} ${reportsCount} direct reports for ${person.name}`}
            className="org-reports-toggle"
            onClick={() => setExpanded((value) => !value)}
            type="button"
          >
            <span>{reportsCount}</span>
            <KeyboardArrowDown fontSize="small" />
          </button>
        ) : null}
      </article>
      {reportsCount && expanded ? (
        <ul className="org-tree-children">
          {node.children.map((child) => <OrganizationTreeNode depth={depth + 1} key={child.person.id} node={child} onSelect={onSelect} />)}
        </ul>
      ) : null}
    </li>
  );
};

const personMatchesTab = (person, activeTab) => {
  if (["Overview", "Directory"].includes(activeTab)) return true;
  if (activeTab === "Employees") return ["Employee", "Intern", "Freelancer", "Contractor"].includes(person.employmentType);
  if (activeTab === "Departments") return Boolean(person.department);
  if (activeTab === "Teams") return Boolean(person.team);
  if (activeTab === "Roles & Access") return true;
  if (activeTab === "Documents") return true;
  if (activeTab === "Attendance" || activeTab === "Leaves" || activeTab === "Performance" || activeTab === "Payroll") return person.status !== "Exited";
  return true;
};

const formatInviteTime = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? ""
    : parsed.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

const getInviteState = (person) => {
  const now = Date.now();
  const resendAt = person?.inviteResendAvailableAt ? new Date(person.inviteResendAvailableAt).getTime() : 0;
  const expiresAt = person?.inviteExpiresAt ? new Date(person.inviteExpiresAt).getTime() : 0;
  const renewalRequestedAt = person?.inviteRenewalRequestedAt ? new Date(person.inviteRenewalRequestedAt).getTime() : 0;
  return {
    isCoolingDown: Boolean(resendAt && resendAt > now),
    isExpired: Boolean(expiresAt && expiresAt <= now),
    resendLabel: resendAt && resendAt > now ? `Resend after ${formatInviteTime(person.inviteResendAvailableAt)}` : "Send invite email",
    expiryLabel: expiresAt ? `Expires ${formatInviteTime(person.inviteExpiresAt)}` : "Invite not sent yet",
    renewalRequested: Boolean(renewalRequestedAt),
    renewalLabel: renewalRequestedAt
      ? `New invitation requested${person?.inviteRenewalRequestedByName ? ` by ${person.inviteRenewalRequestedByName}` : ""} · ${formatInviteTime(person.inviteRenewalRequestedAt)}`
      : "",
  };
};

const groupPeopleBy = (people, field, fallback = "Unassigned") =>
  people.reduce((groups, person) => {
    const key = person[field] || fallback;
    if (!groups[key]) groups[key] = [];
    groups[key].push(person);
    return groups;
  }, {});

function PeopleDocumentationView() {
  return (
    <>
      <section className="module-header-card">
        <SectionHeader
          eyebrow="People"
          title="Employee profiles, invites, and access"
          text="People connects Lyriclious accounts, workspace invitations, HR details, departments, teams, and module permissions."
        />
      </section>
      <section className="split-layout">
        <div className="panel">
          <SectionHeader
            eyebrow="Directory"
            title="What the logged-in admin can manage"
            text="Admins can search existing Lyriclious users by email, invite new people, resend invites, edit employment details, and control module access."
          />
          <div className="documentation-table-scroll" tabIndex="0">
            <div className="people-table documentation-people-table">
              <div className="documentation-people-heading"><span>Person</span><span>Relationship</span><span>Capacity</span><span>Status</span></div>
              {peopleRows.map((person) => (
                <div className="person-row" key={person.name}>
                  <div>
                    <strong>{person.name}</strong>
                    <span>{person.role}</span>
                  </div>
                  <p>{person.relation}</p>
                  <small>{person.load}</small>
                  <em>{person.status}</em>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="panel">
          <SectionHeader
            eyebrow="Access"
            title="Flexible relationships"
            text="Companies can keep a flat team or use departments, managers, custom roles, and HR/admin access."
          />
          <div className="relationship-list">
            {relationshipModels.map((model) => (
              <article className="info-card" key={model.title}>
                <h3>{model.title}</h3>
                <p>{model.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="panel documentation-leave-panel">
        <div className="documentation-leave-header">
          <div>
            <span>Leave management</span>
            <h3>Clear requests, team coverage, and approvals</h3>
            <p>Employees submit a short request; managers see the dates, handover note, and team impact before making a decision.</p>
          </div>
          <span className="documentation-leave-icon"><CalendarMonth fontSize="small" /></span>
        </div>
        <div className="documentation-leave-grid">
          <article>
            <span className="documentation-leave-step">1</span>
            <div><strong>Request time away</strong><p>Choose the leave type, dates, and a concise handover note. Requests route to the assigned reporting approver or the workspace owner.</p></div>
          </article>
          <article>
            <span className="documentation-leave-step">2</span>
            <div><strong>Check availability</strong><p>Review overlapping leave and project coverage before approving.</p></div>
          </article>
          <article>
            <span className="documentation-leave-step success"><CheckCircleOutline fontSize="small" /></span>
            <div><strong>Keep everyone informed</strong><p>The final decision remains visible to the employee and their approver.</p></div>
          </article>
        </div>
      </section>
    </>
  );
}

export function PeopleView({ activeTab = "Overview", setActiveTab, documentationMode = false, moduleTabs = [], records = [], onCreateRecord, onUpdateRecord, defaultOwner = "", currentUser = null, activity = [], selectedProjectName = "", selectedProjectId = "", searchQuery = "" }) {
  return documentationMode ? (
    <PeopleDocumentationView />
  ) : (
    <LivePeopleView activeTab={activeTab} activity={activity} currentUser={currentUser} defaultOwner={defaultOwner} moduleTabs={moduleTabs} onCreateRecord={onCreateRecord} onUpdateRecord={onUpdateRecord} records={records} searchQuery={searchQuery} selectedProjectName={selectedProjectName} selectedProjectId={selectedProjectId} setActiveTab={setActiveTab} />
  );
}

function LivePeopleView({ activeTab = "Overview", setActiveTab, moduleTabs = [], records = [], onCreateRecord, onUpdateRecord, defaultOwner = "", currentUser = null, activity = [], selectedProjectName = "", selectedProjectId = "", searchQuery = "" }) {
  const [people, setPeople] = useState([]);
  const [meta, setMeta] = useState(fallbackMeta);
  const [query, setQuery] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [selectedInviteUser, setSelectedInviteUser] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [personDialogMode, setPersonDialogMode] = useState("invite");
  const [form, setForm] = useState(emptyPersonForm);
  const [departmentForm, setDepartmentForm] = useState({ id: "", name: "", description: "", status: "Active", projectRefs: [] });
  const [teamForm, setTeamForm] = useState({ id: "", name: "", department: "", description: "", status: "Active", projectRefs: [] });
  const [roleForm, setRoleForm] = useState({
    id: "",
    name: "",
    description: "",
    accessLevel: "Member",
    moduleAccess: ["Home", "Work"],
    permissionsText: "",
  });
  const [customDepartmentOpen, setCustomDepartmentOpen] = useState(false);
  const [customTeamOpen, setCustomTeamOpen] = useState(false);
  const [pendingPersonDepartment, setPendingPersonDepartment] = useState(false);
  const [pendingPersonTeam, setPendingPersonTeam] = useState(false);
  const [departmentModalOpen, setDepartmentModalOpen] = useState(false);
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pendingInvitePersonId, setPendingInvitePersonId] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [leaveFormOpen, setLeaveFormOpen] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ type: "Casual Leave", start: "", end: "", reason: "" });
  const [editingLeaveRequest, setEditingLeaveRequest] = useState(null);
  const [leaveDecisionOpen, setLeaveDecisionOpen] = useState(false);
  const [selectedLeaveRequest, setSelectedLeaveRequest] = useState(null);
  const [flowFormOpen, setFlowFormOpen] = useState(false);
  const [flowForm, setFlowForm] = useState({ name: "", trigger: "Leave request", approver: "Reporting manager", mode: "Sequential" });
  const [attendance, setAttendance] = useState([]);
  const [attendanceSettings, setAttendanceSettings] = useState({ autoMarkEnabled: false, autoMarkMinutes: 30 });
  const [attendanceSettingsOpen, setAttendanceSettingsOpen] = useState(false);
  const [organizationZoom, setOrganizationZoom] = useState(1);

  const leaveRequests = useMemo(() => records.filter((record) => record.moduleName === "People" && /leave/i.test(record.type)), [records]);
  const flowRecords = useMemo(() => records.filter((record) => record.moduleName === "People" && record.tab === "Flows"), [records]);
  const currentPerson = useMemo(() => {
    const currentUserId = String(currentUser?.id || currentUser?._id || "");
    const currentEmail = String(currentUser?.email || "").trim().toLowerCase();
    return people.find((person) => String(person.userId || "") === currentUserId || (currentEmail && String(person.email || "").trim().toLowerCase() === currentEmail)) || null;
  }, [currentUser?.email, currentUser?.id, currentUser?._id, people]);
  const canManagePeople = currentUser?.canManagePeople === true || currentUser?.accessLevel === "Owner";
  const leaveApprovers = currentPerson?.leaveApprovers?.length ? currentPerson.leaveApprovers : [currentPerson?.managerName].filter(Boolean);
  const leaveApprover = leaveApprovers.join(" | ");
  const canApproveLeaveRequest = (request) => {
    const target = String(request?.approvalRole || "").trim().toLowerCase();
    const identities = [currentPerson?.name, currentUser?.name, currentUser?.email].filter(Boolean).map((value) => String(value).trim().toLowerCase());
    const access = [currentPerson?.accessLevel, currentPerson?.workspaceRole, currentUser?.accessLevel, currentUser?.workspaceRole, currentUser?.role].filter(Boolean).join(" ").toLowerCase();
    return target.split("|").map((value) => value.trim()).some((approver) => identities.includes(approver) || (approver === "workspace owner" && /owner|admin/.test(access)));
  };
  const isOwnLeaveRequest = (request) => String(request?.owner || "").trim().toLowerCase() === String(currentUser?.name || defaultOwner || "").trim().toLowerCase();
  const openLeaveEditor = (request) => {
    const match = String(request.description || "").match(/^(\d{4}-\d{2}-\d{2}) to (\d{4}-\d{2}-\d{2})\s*—\s*(.*)$/);
    setEditingLeaveRequest(request);
    setLeaveForm({ type: String(request.title || "Casual Leave").split(" · ")[0], start: match?.[1] || request.due || "", end: match?.[2] || request.due || "", reason: match?.[3] || "" });
    setLeaveFormOpen(true);
  };

  const submitLeave = async (event) => {
    event.preventDefault();
    if (!leaveForm.start || !leaveForm.end || !leaveForm.reason.trim()) return;
    setSaving(true);
    setError("");
    try {
      const payload = {
        moduleName: "People", tab: "Leaves", type: "Leave Request", status: "Pending",
        title: `${leaveForm.type} · ${defaultOwner || "Employee"}`, owner: defaultOwner,
        due: leaveForm.start, description: `${leaveForm.start} to ${leaveForm.end} — ${leaveForm.reason.trim()}`,
        approvalRole: leaveApprover || "Workspace owner", priority: leaveForm.type === "Emergency Leave" ? "High" : "Medium",
      };
      if (editingLeaveRequest?.id) await onUpdateRecord?.(editingLeaveRequest.id, payload);
      else await onCreateRecord?.(payload);
      setLeaveForm({ type: "Casual Leave", start: "", end: "", reason: "" });
      setEditingLeaveRequest(null);
      setLeaveFormOpen(false);
      setMessage(editingLeaveRequest ? "Leave request updated." : "Leave request submitted for approval.");
    } catch (submitError) {
      setError(submitError?.response?.data?.message || "Unable to submit leave request.");
    } finally { setSaving(false); }
  };

  const decideLeaveRequest = async (status) => {
    if (!selectedLeaveRequest?.id) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await onUpdateRecord?.(selectedLeaveRequest.id, { status });
      setMessage(`Leave request ${status.toLowerCase()}.`);
      setLeaveDecisionOpen(false);
      setSelectedLeaveRequest(null);
    } catch (decisionError) {
      setError(decisionError?.response?.data?.message || "Unable to update leave request.");
    } finally {
      setSaving(false);
    }
  };

  const submitFlow = async (event) => {
    event.preventDefault();
    if (!flowForm.name.trim()) return;
    setSaving(true); setError("");
    try {
      await onCreateRecord?.({ moduleName: "People", tab: "Flows", type: "Approval Flow", title: flowForm.name.trim(), status: "Active", priority: "Medium", owner: defaultOwner, approvalRole: flowForm.approver, description: `Trigger: ${flowForm.trigger}. Routing: ${flowForm.mode}.` });
      setFlowForm({ name: "", trigger: "Leave request", approver: "Reporting manager", mode: "Sequential" }); setFlowFormOpen(false); setMessage("Approval flow created.");
    } catch (flowError) { setError(flowError?.response?.data?.message || "Unable to create approval flow."); }
    finally { setSaving(false); }
  };

  const loadPeople = useCallback(async () => {
    setLoading(true);
    setError("");
    setPeople([]);
    setMeta(fallbackMeta);
    try {
      const data = await getWorkspacePeople(selectedProjectName, selectedProjectId);
      const loadedPeople = data.people || [];
      // The API creates/returns the persisted owner profile. Keeping only those
      // records prevents temporary client IDs from being used in edit requests.
      setPeople(loadedPeople);
      setMeta({ ...fallbackMeta, ...(data.meta || {}) });
      setLoading(false);
      getWorkspaceAttendance()
        .then((attendanceData) => {
          setAttendance(attendanceData.attendance || []);
          setAttendanceSettings(attendanceData.settings || { autoMarkEnabled: false, autoMarkMinutes: 30 });
        })
        .catch(() => setAttendance([]));
    } catch (loadError) {
      setError(loadError?.response?.data?.message || "Unable to load people.");
    } finally { setLoading(false); }
  }, [selectedProjectId, selectedProjectName]);

  const markAttendance = async (action) => {
    setSaving(true); setError("");
    try { const data = await clockWorkspaceAttendance(action); setAttendance((rows) => [...rows.filter((row) => row.personId !== data.attendance.personId), data.attendance]); setMessage(`Attendance marked ${action}.`); }
    catch (attendanceError) { setError(attendanceError?.response?.data?.message || "Unable to update attendance."); }
    finally { setSaving(false); }
  };

  const saveAttendanceSettings = async () => {
    setSaving(true); setError("");
    try { const data = await updateWorkspaceAttendanceSettings(attendanceSettings); setAttendanceSettings(data.settings); setMessage("Automatic attendance settings saved."); }
    catch (settingsError) { setError(settingsError?.response?.data?.message || "Unable to save attendance settings."); }
    finally { setSaving(false); }
  };

  useEffect(() => {
    loadPeople();
  }, [loadPeople]);

  useEffect(() => {
    setQuery(searchQuery || "");
  }, [searchQuery]);

  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(async () => {
      const value = userQuery.trim();
      if (value.length < 2) {
        setUserResults([]);
        return;
      }

      try {
        const data = await searchWorkspacePeopleUsers(value);
        if (active) setUserResults(data.users || []);
      } catch (searchError) {
        if (active) setUserResults([]);
      }
    }, 220);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [userQuery]);

  const filteredPeople = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return people
      .filter((person) => personMatchesTab(person, activeTab))
      .filter((person) =>
        `${person.name} ${person.email} ${person.employeeCode} ${person.department} ${person.team} ${person.designation} ${person.workspaceRole} ${person.accessLevel} ${person.status} ${person.location}`
          .toLowerCase()
          .includes(needle)
      );
  }, [activeTab, people, query]);
  const searchCopy = useMemo(() => ({
    Overview: ["Search people", "Name, email, role, team, department, status, or location"],
    Directory: ["Search directory", "Name, email, role, department"],
    Organization: ["Search organization", "Name, role, team, or department"],
    Departments: ["Search departments", "Department or member"],
    Teams: ["Search teams", "Team or member"],
    "Roles & Access": ["Search people and roles", "Name, email, role, department"],
    Flows: ["Search approval flows", "Flow name, trigger, or approver"],
    Attendance: ["Search attendance", "Name, team, department"],
    Leaves: ["Search leave availability", "Name, team, department"],
    Performance: ["Search performance profiles", "Name, team, department"],
    Assets: ["Search asset assignments", "Name, team, department"],
    Documents: ["Search document owners", "Name, team, department"],
    Payroll: ["Search payroll profiles", "Name, team, department"],
  }[activeTab] || ["Search people", "Name, email, role, department"]), [activeTab]);
  const tabHero = useMemo(() => ({
    Overview: "A concise view of workforce size, invitations, access coverage, and department structure.",
    Directory: "Find people, maintain employment details, and manage workspace membership.",
    Organization: "View the reporting structure created from the manager assigned to each person.",
    Departments: "Define the business units that organize people, reporting, and access scope.",
    Teams: "Organize delivery teams and keep their departmental ownership clear.",
    "Roles & Access": "Manage reusable role presets, permission scope, and workspace access.",
    Flows: "Set approval routes for leave, attendance corrections, documents, assets, payroll, and performance decisions.",
    Attendance: "Monitor availability and maintain accurate attendance records.",
    Leaves: "Submit, review, and track leave requests and availability.",
    Performance: "Keep performance information and review activity organized by person.",
    Assets: "Track equipment responsibility and asset-related people records.",
    Documents: "Manage employment documents and their approved access.",
    Payroll: "Review payroll-related people records with appropriate access controls.",
  }[activeTab] || "Manage people operations for this workspace."), [activeTab]);

  const renderHeroAction = () => {
    if (activeTab === "Directory" && canManagePeople) return <button className="primary-action people-add-button" onClick={() => openCreate()} type="button"><PersonAddAlt fontSize="small" />Invite person</button>;
    if (activeTab === "Organization" && canManagePeople) return <button className="primary-action people-add-button" onClick={() => openCreate()} type="button"><PersonAddAlt fontSize="small" />Invite person</button>;
    if (activeTab === "Departments" && canManagePeople) return <button className="primary-action people-add-button" onClick={() => { setDepartmentForm({ id: "", name: "", description: "", status: "Active", projectRefs: selectedProjectName ? [selectedProjectName] : [] }); setDepartmentModalOpen(true); }} type="button"><Add fontSize="small" />Add department</button>;
    if (activeTab === "Teams" && canManagePeople) return <button className="primary-action people-add-button" onClick={() => { setTeamForm({ id: "", name: "", department: "", description: "", status: "Active", projectRefs: selectedProjectName ? [selectedProjectName] : [] }); setTeamModalOpen(true); }} type="button"><Add fontSize="small" />Create team</button>;
    if (activeTab === "Roles & Access" && canManagePeople) return <button className="primary-action people-add-button" onClick={() => { setRoleForm({ id: "", name: "", description: "", accessLevel: "Member", moduleAccess: ["Home", "Work"], permissionsText: "" }); setRoleModalOpen(true); }} type="button"><Add fontSize="small" />Add role</button>;
    if (activeTab === "Flows") return <button className="primary-action people-add-button" onClick={() => setFlowFormOpen(true)} type="button"><Add fontSize="small" />Create flow</button>;
    if (activeTab === "Leaves") return <button className="primary-action people-add-button" onClick={() => setLeaveFormOpen(true)} type="button"><Add fontSize="small" />Apply for leave</button>;
    return null;
  };

  const departmentRows = useMemo(() => meta.departmentRows || [], [meta.departmentRows]);
  const teamRows = useMemo(() => meta.teamRows || [], [meta.teamRows]);
  const roleTemplateRows = useMemo(() => meta.roleTemplates || fallbackMeta.roleTemplates, [meta.roleTemplates]);
  const availableProjects = useMemo(() => meta.availableProjects || [], [meta.availableProjects]);
  const visibleTabs = useMemo(() => {
    const permissions = meta.permissions || {};
    const configuredTabs = moduleTabs.length
      ? moduleTabs.map((tab) => (typeof tab === "string" ? tab : tab.name || tab.label)).filter(Boolean)
      : ["Overview", "Directory", "Organization", "Departments", "Teams", "Attendance", "Leaves", "Performance", "Assets", "Documents"];
    return configuredTabs
      .filter((tab) => permissions[tab] !== false);
  }, [meta.permissions, moduleTabs]);
  const accessDenied = !loading && Boolean(meta.permissions) && meta.permissions[activeTab] === false;

  const departmentOptions = useMemo(
    () => Array.from(new Set([
      ...(meta.departments || []),
      ...departmentRows.map((department) => department.name),
      ...people.map((person) => person.department),
    ].filter(Boolean))).sort(),
    [departmentRows, meta.departments, people]
  );

  const teamOptions = useMemo(
    () => Array.from(new Set([
      ...(meta.teams || []),
      ...teamRows.map((team) => team.name),
      ...people.map((person) => person.team),
    ].filter(Boolean))).sort(),
    [meta.teams, people, teamRows]
  );

  const managerOptions = useMemo(
    () => people
      .filter((person) => !["Disabled", "Exited"].includes(person.status))
      .filter((person) => person.id !== form.id && person.userId !== form.userId)
      .filter((person) => /^[a-f\d]{24}$/i.test(`${person.userId || ""}`))
      .sort((first, second) => first.name.localeCompare(second.name)),
    [form.id, form.userId, people]
  );

  const organizationTree = useMemo(() => {
    const normalize = (value) => String(value || "").trim().toLowerCase();
    const nodes = people.map((person) => ({ children: [], person }));
    const peopleByIdentity = new Map();
    nodes.forEach((node) => {
      [node.person.id, node.person.name, node.person.email].map(normalize).filter(Boolean)
        .forEach((identity) => peopleByIdentity.set(identity, node));
    });
    const roots = [];
    nodes.forEach((node) => {
      const manager = peopleByIdentity.get(normalize(node.person.managerName));
      if (manager && manager.person.id !== node.person.id) manager.children.push(node);
      else roots.push(node);
    });
    return roots.length ? roots : nodes;
  }, [people]);

  const stats = useMemo(() => {
    const active = people.filter((person) => person.status === "Active").length;
    const invited = people.filter((person) => person.status === "Invited").length;
    const admins = people.filter((person) => ["Owner", "Admin"].includes(person.accessLevel)).length;
    return [
      { label: "People", value: people.length, note: `${active} active` },
      { label: "Invites", value: invited, note: "Pending acceptance" },
      { label: "Admins", value: admins, note: "Owner/Admin access" },
      { label: "Departments", value: departmentOptions.length, note: selectedProjectName ? `Configured for ${selectedProjectName}` : "Configured departments" },
    ];
  }, [departmentOptions.length, people, selectedProjectName]);

  const openCreate = () => {
    setPersonDialogMode("invite");
    setForm({ ...emptyPersonForm, projectRefs: selectedProjectName ? [selectedProjectName] : [] });
    setCustomDepartmentOpen(false);
    setCustomTeamOpen(false);
    setUserQuery("");
    setUserResults([]);
    setSelectedInviteUser(null);
    setMessage("");
    setError("");
    setFormOpen(true);
  };

  const openEdit = (person) => {
    if (person.isVirtual || !/^[a-f\d]{24}$/i.test(`${person.id || ""}`)) {
      setError("This owner card is still being set up. Refresh People, then edit the saved profile from Directory.");
      return;
    }
    setPersonDialogMode("edit");
    setForm({
      ...emptyPersonForm,
      ...person,
      moduleAccess: person.moduleAccess?.length ? person.moduleAccess : emptyPersonForm.moduleAccess,
    });
    setCustomDepartmentOpen(false);
    setCustomTeamOpen(false);
    setUserQuery(person.email || "");
    setUserResults([]);
    setSelectedInviteUser(person.linkedUser || null);
    setMessage("");
    setError("");
    setFormOpen(true);
  };

  const openTransfer = (person) => {
    openEdit(person);
    setPersonDialogMode("transfer");
  };

  const selectUser = (user) => {
    const existingPerson = people.find(
      (person) => person.userId === user.id || person.email?.toLowerCase() === user.email?.toLowerCase()
    );
    setForm((current) => ({
      ...current,
      userId: user.id,
      name: user.name || existingPerson?.name || current.name,
      email: user.email || existingPerson?.email || current.email,
      employmentType: existingPerson?.employmentType || current.employmentType,
      department: existingPerson?.department || current.department,
      team: existingPerson?.team || current.team,
      designation: user.designation || existingPerson?.designation || current.designation,
      managerName: existingPerson?.managerName || current.managerName,
      managerUser: existingPerson?.managerUser || current.managerUser,
      workspaceRole: existingPerson?.workspaceRole || current.workspaceRole,
      accessLevel: existingPerson?.accessLevel || current.accessLevel,
      moduleAccess: existingPerson?.moduleAccess?.length ? existingPerson.moduleAccess : current.moduleAccess,
      status: existingPerson?.status || current.status,
      joiningDate: existingPerson?.joiningDate || current.joiningDate,
      phone: user.phone || existingPerson?.phone || current.phone,
      location: user.location || existingPerson?.location || current.location,
      notes: existingPerson?.notes || current.notes,
      projectRefs: existingPerson?.projectRefs?.length ? existingPerson.projectRefs : current.projectRefs,
    }));
    setSelectedInviteUser(user);
    setUserQuery(user.email || user.name || "");
    setUserResults([]);
  };

  const updateForm = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const chooseDepartment = (event) => {
    const value = event.target.value;
    if (value === "__new__") {
      setForm((current) => ({ ...current, department: "" }));
      setCustomDepartmentOpen(true);
      return;
    }
    setCustomDepartmentOpen(false);
    setForm((current) => ({ ...current, department: value }));
  };

  const chooseTeam = (event) => {
    const value = event.target.value;
    if (value === "__new__") {
      setForm((current) => ({ ...current, team: "" }));
      setCustomTeamOpen(true);
      return;
    }
    setCustomTeamOpen(false);
    setForm((current) => ({ ...current, team: value }));
  };

  const toggleModule = (moduleKey) => {
    setForm((current) => {
      const moduleAccess = current.moduleAccess.includes(moduleKey)
        ? current.moduleAccess.filter((item) => item !== moduleKey)
        : [...current.moduleAccess, moduleKey];
      return { ...current, moduleAccess };
    });
  };

  const upsertMetaRow = (key, row) => {
    setMeta((current) => {
      const rows = current[key] || [];
      const exists = rows.some((item) => item.id === row.id);
      return {
        ...current,
        [key]: exists ? rows.map((item) => (item.id === row.id ? row : item)) : [row, ...rows],
      };
    });
  };

  const saveDepartment = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = {
        name: departmentForm.name,
        description: departmentForm.description,
        status: departmentForm.status,
        projectRefs: departmentForm.projectRefs,
        projectIds: selectedProjectId ? [selectedProjectId] : [],
      };
      const data = departmentForm.id
        ? await updateWorkspaceDepartment(departmentForm.id, payload)
        : await createWorkspaceDepartment(payload);
      upsertMetaRow("departmentRows", data.department);
      setMeta((current) => ({
        ...current,
        departments: Array.from(new Set([...(current.departments || []), data.department.name])).sort(),
      }));
      setDepartmentForm({ id: "", name: "", description: "", status: "Active", projectRefs: selectedProjectName ? [selectedProjectName] : [] });
      setDepartmentModalOpen(false);
      if (pendingPersonDepartment) {
        setForm((current) => ({ ...current, department: data.department.name }));
        setPendingPersonDepartment(false);
      }
      setMessage("Department saved.");
      if (departmentForm.id) await loadPeople();
    } catch (saveError) {
      setError(saveError?.response?.data?.message || "Unable to save department.");
    } finally {
      setSaving(false);
    }
  };

  const saveTeam = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = {
        name: teamForm.name,
        department: teamForm.department,
        description: teamForm.description,
        status: teamForm.status,
        projectRefs: teamForm.projectRefs,
        projectIds: selectedProjectId ? [selectedProjectId] : [],
      };
      const data = teamForm.id
        ? await updateWorkspaceTeam(teamForm.id, payload)
        : await createWorkspaceTeam(payload);
      upsertMetaRow("teamRows", data.team);
      setMeta((current) => ({
        ...current,
        teams: Array.from(new Set([...(current.teams || []), data.team.name])).sort(),
      }));
      setTeamForm({ id: "", name: "", department: "", description: "", status: "Active", projectRefs: selectedProjectName ? [selectedProjectName] : [] });
      setTeamModalOpen(false);
      if (pendingPersonTeam) {
        setForm((current) => ({ ...current, team: data.team.name }));
        setPendingPersonTeam(false);
      }
      setMessage("Team saved.");
      if (teamForm.id) await loadPeople();
    } catch (saveError) {
      setError(saveError?.response?.data?.message || "Unable to save team.");
    } finally {
      setSaving(false);
    }
  };

  const saveRoleTemplate = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = {
        name: roleForm.name,
        description: roleForm.description,
        accessLevel: roleForm.accessLevel,
        moduleAccess: roleForm.moduleAccess,
        permissions: roleForm.permissionsText
          .split(",")
          .map((permission) => permission.trim())
          .filter(Boolean),
      };
      const data = roleForm.id
        ? await updateWorkspaceRoleTemplate(roleForm.id, payload)
        : await createWorkspaceRoleTemplate(payload);
      upsertMetaRow("roleTemplates", data.roleTemplate);
      setRoleForm({
        id: "",
        name: "",
        description: "",
        accessLevel: "Member",
        moduleAccess: ["Home", "Work"],
        permissionsText: "",
      });
      setRoleModalOpen(false);
      setMessage("Role preset saved.");
    } catch (saveError) {
      setError(saveError?.response?.data?.message || "Unable to save role preset.");
    } finally {
      setSaving(false);
    }
  };

  const cloneRoleTemplate = async (role) => {
    setSaving(true); setError(""); setMessage("");
    try {
      const data = await cloneWorkspaceRoleTemplate(role.id, { name: `${role.name} copy` });
      upsertMetaRow("roleTemplates", data.roleTemplate);
      setMessage("Role cloned. Update its permissions before assigning it.");
    } catch (cloneError) { setError(cloneError?.response?.data?.message || "Unable to clone role."); }
    finally { setSaving(false); }
  };

  const archiveRoleTemplate = async (role) => {
    setSaving(true); setError(""); setMessage("");
    try {
      await archiveWorkspaceRoleTemplate(role.id);
      setMeta((current) => ({ ...current, roleTemplates: (current.roleTemplates || []).filter((item) => item.id !== role.id) }));
      setMessage("Role archived. Existing assignments remain auditable.");
    } catch (archiveError) { setError(archiveError?.response?.data?.message || "Unable to archive role."); }
    finally { setSaving(false); }
  };

  const chooseRoleTemplate = (event) => {
    const roleName = event.target.value;
    const roleTemplate = roleTemplateRows.find((role) => role.name === roleName);
    setForm((current) => ({
      ...current,
      workspaceRole: roleName,
      accessLevel: roleTemplate?.accessLevel || current.accessLevel,
      moduleAccess: roleTemplate?.moduleAccess?.length ? roleTemplate.moduleAccess : current.moduleAccess,
    }));
  };

  const toggleRoleTemplateModule = (moduleKey) => {
    setRoleForm((current) => {
      const moduleAccess = current.moduleAccess.includes(moduleKey)
        ? current.moduleAccess.filter((item) => item !== moduleKey)
        : [...current.moduleAccess, moduleKey];
      return { ...current, moduleAccess };
    });
  };

  const submitPerson = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");

    try {
      if (form.department && !departmentOptions.includes(form.department)) {
        const data = await createWorkspaceDepartment({ name: form.department, projectRefs: selectedProjectName ? [selectedProjectName] : form.projectRefs, projectIds: selectedProjectId ? [selectedProjectId] : [] });
        upsertMetaRow("departmentRows", data.department);
      }

      if (form.team && !teamOptions.includes(form.team)) {
        const data = await createWorkspaceTeam({ name: form.team, department: form.department, projectRefs: selectedProjectName ? [selectedProjectName] : form.projectRefs, projectIds: selectedProjectId ? [selectedProjectId] : [] });
        upsertMetaRow("teamRows", data.team);
      }

      const payload = {
        // Do not send virtual/legacy workspace identifiers to the User `_id`
        // endpoint. The API can still link the account from the person's email.
        userId: /^[a-f\d]{24}$/i.test(`${form.userId || ""}`) ? form.userId : "",
        name: form.name,
        email: form.email,
        employmentType: form.employmentType,
        department: form.department,
        team: form.team,
        designation: form.designation,
        managerName: form.managerName,
        managerUser: /^[a-f\d]{24}$/i.test(`${form.managerUser || ""}`) ? form.managerUser : "",
        workspaceRole: form.workspaceRole,
        accessLevel: form.accessLevel,
        moduleAccess: form.moduleAccess,
        status: form.status,
        joiningDate: form.joiningDate,
        phone: form.phone,
        location: form.location,
        notes: form.notes,
        projectRefs: selectedProjectName ? [selectedProjectName] : form.projectRefs,
        projectIds: selectedProjectId ? [selectedProjectId] : [],
        projectContext: Boolean(selectedProjectName || selectedProjectId),
      };
      const data = form.id
        ? await updateWorkspacePerson(form.id, payload)
        : await createWorkspacePerson(payload);
      const sentAt = data.inviteEmailSent === true && !data.person?.inviteSentAt
        ? new Date().toISOString()
        : data.person?.inviteSentAt;
      const savedPerson = {
        ...data.person,
        inviteSentAt: sentAt || null,
        inviteExpiresAt: data.inviteEmailSent === true && !data.person?.inviteExpiresAt
          ? new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString()
          : data.person?.inviteExpiresAt || null,
        inviteResendAvailableAt: data.inviteEmailSent === true && !data.person?.inviteResendAvailableAt
          ? new Date(Date.now() + 5 * 60 * 1000).toISOString()
          : data.person?.inviteResendAvailableAt || null,
      };
      setPeople((current) => {
        const exists = current.some((person) => person.id === savedPerson.id);
        return exists
          ? current.map((person) => (person.id === savedPerson.id ? savedPerson : person))
          : [savedPerson, ...current];
      });
      setMessage(data.message || (personDialogMode === "transfer" ? "Person transferred." : form.id ? "Person updated." : "Person invited."));
      setFormOpen(false);
    } catch (submitError) {
      setError(submitError?.response?.data?.message || submitError?.message || "Unable to save this person.");
    } finally {
      setSaving(false);
    }
  };

  const resendInvite = async (person) => {
    const personId = String(person?.id || "");
    if (!/^[a-f\d]{24}$/i.test(personId)) {
      setError("This workspace owner entry is managed automatically and does not require an invite email.");
      return;
    }
    setSaving(true);
    setPendingInvitePersonId(personId);
    setError("");
    setMessage("");
    try {
      const data = await resendWorkspacePersonInvite(personId);
      setPeople((current) => current.map((item) => (String(item.id || "") === personId ? data.person : item)));
      setMessage(data.message || `Invite sent to ${person.email}.`);
    } catch (inviteError) {
      setError(inviteError?.response?.data?.message || "Unable to resend invite.");
    } finally {
      setPendingInvitePersonId((current) => (current === personId ? "" : current));
      setSaving(false);
    }
  };

  const openCreateWith = (patch) => {
    openCreate();
    setForm((current) => ({ ...current, ...patch }));
    if (patch.department) setCustomDepartmentOpen(!departmentOptions.includes(patch.department));
    if (patch.team) setCustomTeamOpen(!teamOptions.includes(patch.team));
  };

  const toggleProjectRef = (projectName) => {
    setForm((current) => {
      const projectRefs = current.projectRefs.includes(projectName)
        ? current.projectRefs.filter((item) => item !== projectName)
        : [...current.projectRefs, projectName];
      return { ...current, projectRefs };
    });
  };

  const renderPersonRows = (rows = filteredPeople, emptyText = "No people found for this section.") => (
    <div className="people-table people-directory-table">
      {loading ? <p className="empty-state"><WorkspaceInlineLoader label="Loading people" /></p> : null}
      {!loading && rows.map((person) => (
        (() => {
          const inviteState = getInviteState(person);
          const isInviteSending = pendingInvitePersonId === String(person.id || "");
          const inviteButtonLabel = isInviteSending
            ? "Sending..."
            : inviteState.isCoolingDown
              ? "Wait"
              : inviteState.renewalRequested
                ? "Resend now"
                : person.inviteSentAt
                  ? "Resend"
                  : "Invite";
          return (
        <div className="person-row" key={person.id}>
          <div>
            <strong>{person.name}</strong>
            <span>{person.email}</span>
          </div>
          <p>{person.designation || person.workspaceRole} · {person.department || "No department"} · {person.team || "No team"}</p>
          <small className={inviteState.renewalRequested ? "invite-renewal-requested" : ""}>{person.status === "Invited" ? `${inviteState.expiryLabel}${inviteState.isExpired ? " · Expired" : ""}${inviteState.renewalRequested ? ` · ${inviteState.renewalLabel}` : ""}` : person.accessLevel}</small>
          <em>{person.status}</em>
          <div className="person-actions">
            {person.isVirtual ? <small>Managed automatically</small> : <>
              <button disabled={saving} onClick={() => openTransfer(person)} type="button">
                <Edit fontSize="small" />
                Edit
              </button>
              <button disabled={saving} onClick={() => openEdit(person)} type="button">
                <Refresh fontSize="small" />
                Transfer
              </button>
              <button aria-busy={isInviteSending} disabled={saving || isInviteSending || inviteState.isCoolingDown} onClick={() => resendInvite(person)} title={isInviteSending ? "Sending invite email..." : inviteState.resendLabel} type="button">
                {isInviteSending ? <WorkspaceInlineLoader compact label="Sending" /> : <><MailOutline fontSize="small" />{inviteButtonLabel}</>}
              </button>
            </>}
          </div>
        </div>
          );
        })()
      ))}
      {!loading && !rows.length ? <p className="empty-state">{emptyText}</p> : null}
    </div>
  );

  const renderGroupCards = (field, title, emptyText) => {
    const groups = groupPeopleBy(filteredPeople, field);
    const configuredRows = field === "department" ? departmentRows : teamRows;
    const configuredNames = configuredRows.map((row) => row.name).filter(Boolean);
    const entries = Array.from(new Set([...configuredNames, ...Object.keys(groups)]))
      .filter(Boolean)
      .filter((groupName) => {
        const needle = query.trim().toLowerCase();
        return !needle || groupName.toLowerCase().includes(needle) || (groups[groupName] || []).length > 0;
      })
      .map((groupName) => [groupName, groups[groupName] || []]);
    return <div className="people-data-table-wrap"><table className="people-data-table"><thead><tr><th>{title}</th><th>Description</th><th>Members</th><th>Leads / admins</th><th>Status</th><th aria-label="Actions" /></tr></thead><tbody>{loading ? <tr><td colSpan="6"><span className="people-table-loader"><WorkspaceInlineLoader label={`Loading ${field === "department" ? "departments" : "teams"}`} /></span></td></tr> : entries.map(([groupName, members]) => {
      const admins = members.filter((person) => ["Owner", "Admin", "Manager"].includes(person.accessLevel)).length;
      const settingRow = configuredRows.find((row) => row.name === groupName);
      return <tr key={groupName}><td><strong>{groupName}</strong></td><td>{settingRow?.description || "—"}</td><td>{members.length}</td><td>{admins}</td><td><span className="people-table-status">{settingRow?.status || "Active"}</span></td><td><div className="person-actions">{settingRow ? <button onClick={() => { if (field === "department") { setDepartmentForm(settingRow); setDepartmentModalOpen(true); } if (field === "team") { setTeamForm(settingRow); setTeamModalOpen(true); } }} type="button"><Edit fontSize="small" />Edit</button> : null}<button onClick={() => openCreateWith(field === "department" ? { department: groupName } : { team: groupName })} type="button"><Add fontSize="small" />Add</button></div></td></tr>;
    })}</tbody></table>{!loading && !entries.length ? <p className="empty-state">{emptyText}</p> : null}</div>;
  };

  const renderOperationalCards = (kind) => {
    const labels = {
      Performance: ["Access readiness", "Role clarity", "Active status"],
      Assets: ["Assigned assets", "Device notes", "Recovery status"],
      Documents: ["Profile docs", "Policy access", "Missing files"],
      Payroll: ["Payroll status", "Access level", "Finance visibility"],
    };
    return <div className="people-data-table-wrap"><table className="people-data-table"><thead><tr><th>Person</th><th>{labels[kind]?.[0]}</th><th>{labels[kind]?.[1]}</th><th>{labels[kind]?.[2]}</th><th>Notes</th><th aria-label="Actions" /></tr></thead><tbody>{filteredPeople.map((person) => <tr key={person.id}><td><strong>{person.name}</strong><small>{person.email}</small></td><td>{person.moduleAccess?.length || 0} modules</td><td>{person.workspaceRole || person.designation || "Not set"}</td><td><span className="people-table-status">{person.status}</span></td><td>{person.notes || "—"}</td><td><button onClick={() => openEdit(person)} type="button">Edit</button></td></tr>)}</tbody></table>{!filteredPeople.length ? <p className="empty-state">No people found for this section.</p> : null}</div>;
  };

  const renderDepartmentSetup = () => (
    <form className="people-setup-form" onSubmit={saveDepartment}>
      <label>
        Department name
        <input
          onChange={(event) => setDepartmentForm((current) => ({ ...current, name: event.target.value }))}
          placeholder="Engineering, HR, Sales"
          required
          value={departmentForm.name}
        />
      </label>
      <label>
        Description
        <input
          onChange={(event) => setDepartmentForm((current) => ({ ...current, description: event.target.value }))}
          placeholder="What this department owns"
          value={departmentForm.description}
        />
      </label>
      <label>
        Status
        <select
          onChange={(event) => setDepartmentForm((current) => ({ ...current, status: event.target.value }))}
          value={departmentForm.status}
        >
          <option>Active</option>
          <option>Inactive</option>
        </select>
      </label>
      <div className="people-setup-actions">
        {departmentForm.id ? (
          <button onClick={() => setDepartmentForm({ id: "", name: "", description: "", status: "Active" })} type="button">
            Clear
          </button>
        ) : null}
        <button disabled={saving || !departmentForm.name.trim()} type="submit">
          {departmentForm.id ? "Save department" : "Create department"}
        </button>
      </div>
    </form>
  );

  const renderTeamSetup = () => (
    <form className="people-setup-form" onSubmit={saveTeam}>
      <label>
        Team name
        <input
          onChange={(event) => setTeamForm((current) => ({ ...current, name: event.target.value }))}
          placeholder="Frontend, Support, Hiring"
          required
          value={teamForm.name}
        />
      </label>
      <label>
        Department
        <select
          onChange={(event) => setTeamForm((current) => ({ ...current, department: event.target.value }))}
          value={teamForm.department}
        >
          <option value="">No department</option>
          {departmentOptions.map((department) => <option key={department}>{department}</option>)}
        </select>
      </label>
      <label>
        Description
        <input
          onChange={(event) => setTeamForm((current) => ({ ...current, description: event.target.value }))}
          placeholder="What this team handles"
          value={teamForm.description}
        />
      </label>
      <label>
        Status
        <select onChange={(event) => setTeamForm((current) => ({ ...current, status: event.target.value }))} value={teamForm.status}>
          <option>Active</option>
          <option>Inactive</option>
        </select>
      </label>
      <div className="people-setup-actions">
        {teamForm.id ? (
          <button onClick={() => setTeamForm({ id: "", name: "", department: "", description: "", status: "Active" })} type="button">
            Clear
          </button>
        ) : null}
        <button disabled={saving || !teamForm.name.trim()} type="submit">
          {teamForm.id ? "Save team" : "Create team"}
        </button>
      </div>
    </form>
  );

  const renderDepartmentDialog = () => (
    <PeopleDialog description="Set the department name, ownership context, and operational status." id="department-modal-title" onClose={() => { setDepartmentModalOpen(false); setPendingPersonDepartment(false); }} open={departmentModalOpen} title={departmentForm.id ? "Edit department" : "Create department"}>
      {renderDepartmentSetup()}
    </PeopleDialog>
  );

  const renderTeamDialog = () => (
    <PeopleDialog description="Assign the team to a department and describe what it is responsible for." id="team-modal-title" onClose={() => { setTeamModalOpen(false); setPendingPersonTeam(false); }} open={teamModalOpen} title={teamForm.id ? "Edit team" : "Create team"}>
      {renderTeamSetup()}
    </PeopleDialog>
  );

  const renderRoleTemplateSetup = () => (
    <form className="people-setup-form people-role-form" onSubmit={saveRoleTemplate}>
      <label>
        Role preset
        <input
          onChange={(event) => setRoleForm((current) => ({ ...current, name: event.target.value }))}
          placeholder="Manager, HR Partner, Finance Lead"
          required
          value={roleForm.name}
        />
      </label>
      <label>
        Access level
        <select onChange={(event) => setRoleForm((current) => ({ ...current, accessLevel: event.target.value }))} value={roleForm.accessLevel}>
          {(meta.accessLevels || fallbackMeta.accessLevels).map((item) => <option key={item}>{item}</option>)}
        </select>
      </label>
      <label className="people-wide-field">
        Description
        <input
          onChange={(event) => setRoleForm((current) => ({ ...current, description: event.target.value }))}
          placeholder="What this preset can manage"
          value={roleForm.description}
        />
      </label>
      <label className="people-wide-field">
        Permission keys
        <input
          onChange={(event) => setRoleForm((current) => ({ ...current, permissionsText: event.target.value }))}
          placeholder="people:view, work:*, analytics:view"
          value={roleForm.permissionsText}
        />
      </label>
      <div className="people-module-access people-wide-field">
        <span>Preset module access</span>
        <div>
          {(meta.modules || fallbackMeta.modules).map((module) => (
            <button
              className={roleForm.moduleAccess.includes(module.key) ? "active" : ""}
              key={module.key}
              onClick={() => toggleRoleTemplateModule(module.key)}
              type="button"
            >
              {module.label}
            </button>
          ))}
        </div>
      </div>
      <div className="people-setup-actions">
        {roleForm.id ? (
          <button
            onClick={() => setRoleForm({
              id: "",
              name: "",
              description: "",
              accessLevel: "Member",
              moduleAccess: ["Home", "Work"],
              permissionsText: "",
            })}
            type="button"
          >
            Clear
          </button>
        ) : null}
        <button disabled={saving || !roleForm.name.trim()} type="submit">
          {roleForm.id ? "Save role preset" : "Create role preset"}
        </button>
      </div>
    </form>
  );

  const renderRolesAccess = () => (
    <section className="functional-panel">
      <div className="functional-panel-header">
        <div>
          <span>Roles & Access</span>
          <h3>Reusable access presets</h3>
        </div>
        <small>{people.filter((person) => ["Owner", "Admin", "Manager"].includes(person.accessLevel)).length} elevated users</small>
      </div>
      <PeopleDialog description="Define the access preset, its modules, and explicit permission keys." id="role-modal-title" onClose={() => setRoleModalOpen(false)} open={roleModalOpen} title={roleForm.id ? "Edit role" : "Create role"}>
        {renderRoleTemplateSetup()}
      </PeopleDialog>
      <details className="people-role-dropdown">
        <summary>Reusable access presets <span>{roleTemplateRows.length}</span></summary>
        <div className="people-data-table-wrap"><table className="people-data-table"><thead><tr><th>Role</th><th>Level</th><th>Modules</th><th>Actions</th></tr></thead><tbody>{roleTemplateRows.map((role) => <tr key={role.id || role.name}><td><strong>{role.name}</strong><small>{role.description || "Custom workspace access preset"}</small></td><td>{role.accessLevel}</td><td>{(role.moduleAccess || []).join(", ") || "—"}</td><td><div className="person-actions"><button onClick={() => { setRoleForm({ id: role.id || "", name: role.name || "", description: role.description || "", accessLevel: role.accessLevel || "Member", moduleAccess: role.moduleAccess?.length ? role.moduleAccess : [], permissionsText: (role.permissions || []).join(", ") }); setRoleModalOpen(true); }} type="button">Edit</button>{!role.isSystem ? <button disabled={saving} onClick={() => cloneRoleTemplate(role)} type="button">Clone</button> : null}{!role.isSystem ? <button disabled={saving} onClick={() => archiveRoleTemplate(role)} type="button">Archive</button> : null}</div></td></tr>)}</tbody></table></div>
      </details>
      <div className="people-data-table-wrap"><table className="people-data-table"><thead><tr><th>Person</th><th>Role</th><th>Access</th><th>Modules</th><th>Status</th><th aria-label="Actions" /></tr></thead><tbody>{filteredPeople.map((person) => <tr key={person.id}><td><strong>{person.name}</strong><small>{person.email}</small></td><td>{person.workspaceRole || "Not set"}</td><td>{person.accessLevel}</td><td>{(person.moduleAccess || []).join(", ") || "—"}</td><td><span className="people-table-status">{person.status}</span></td><td><button onClick={() => openEdit(person)} type="button">Edit access</button></td></tr>)}</tbody></table>{!filteredPeople.length ? <p className="empty-state">No people available for access management.</p> : null}</div>
    </section>
  );

  const renderTabContent = () => {
    if (accessDenied) {
      return <section className="panel"><SectionHeader eyebrow="People" title="Access denied" text="Your workspace administrator has not granted access to this People area." /></section>;
    }
    if (activeTab === "Overview") return null;

    if (activeTab === "Organization") {
      const zoomIn = () => setOrganizationZoom((value) => Math.min(1.5, Number((value + 0.1).toFixed(1))));
      const zoomOut = () => setOrganizationZoom((value) => Math.max(0.7, Number((value - 0.1).toFixed(1))));
      return <section className="organization-tree-panel"><div className="organization-tree-toolbar"><span>Organisation chart</span><div className="organization-zoom-controls" aria-label="Organisation chart zoom controls"><button aria-label="Zoom out" disabled={organizationZoom <= 0.7} onClick={zoomOut} type="button"><ZoomOut fontSize="small" /></button><output aria-label="Current zoom">{Math.round(organizationZoom * 100)}%</output><button aria-label="Zoom in" disabled={organizationZoom >= 1.5} onClick={zoomIn} type="button"><ZoomIn fontSize="small" /></button><button aria-label="Reset zoom" disabled={organizationZoom === 1} onClick={() => setOrganizationZoom(1)} type="button"><RestartAlt fontSize="small" /></button></div></div><div className="organization-tree-scroll">{people.length ? <div className="organization-tree-canvas" style={{ zoom: organizationZoom }}><ul className="org-tree-roots">{organizationTree.map((node) => <OrganizationTreeNode key={node.person.id} node={node} onSelect={openEdit} />)}</ul></div> : <p className="empty-state">Add people and assign a reporting manager to build the organization chart.</p>}</div></section>;
    }

    if (activeTab === "Departments") {
      return (
        <section className="functional-panel">
          <div className="functional-panel-header">
            <div>
              <span>Departments</span>
              <h3>Manage department groups</h3>
            </div>
            <small>{departmentRows.length} configured</small>
          </div>
          {renderGroupCards("department", "Department", "No departments yet.")}
        </section>
      );
    }

    if (activeTab === "Teams") {
      return (
        <section className="functional-panel">
          <div className="functional-panel-header">
            <div>
              <span>Teams</span>
              <h3>Manage teams</h3>
            </div>
            <button className="primary-action" onClick={() => {
              setTeamForm({ id: "", name: "", department: "", description: "", status: "Active" });
              setTeamModalOpen(true);
            }} type="button">Create team</button>
          </div>
          {renderGroupCards("team", "Team", "No teams yet.")}
        </section>
      );
    }

    if (activeTab === "Roles & Access") {
      return renderRolesAccess();
    }

    if (activeTab === "Flows") {
      return <section className="functional-panel">
        {flowFormOpen ? <ModalPortal><div className="workspace-modal-backdrop" onMouseDown={() => setFlowFormOpen(false)} role="presentation"><section aria-modal="true" className="workspace-modal" onMouseDown={(event) => event.stopPropagation()} role="dialog"><header><div><span>People</span><h3>Create approval flow</h3></div><button aria-label="Close" onClick={() => setFlowFormOpen(false)} type="button">×</button></header><form className="people-setup-form" onSubmit={submitFlow}><label>Flow name<input required onChange={(event) => setFlowForm((current) => ({ ...current, name: event.target.value }))} value={flowForm.name} /></label><label>Trigger<select onChange={(event) => setFlowForm((current) => ({ ...current, trigger: event.target.value }))} value={flowForm.trigger}><option>Leave request</option><option>Attendance correction</option><option>Document approval</option><option>Asset request</option><option>Payroll approval</option><option>Performance review</option></select></label><label>Approver<select onChange={(event) => setFlowForm((current) => ({ ...current, approver: event.target.value }))} value={flowForm.approver}><option>Reporting manager</option><option>Department head</option><option>HR</option><option>Finance</option><option>Workspace admin</option></select></label><label>Routing<select onChange={(event) => setFlowForm((current) => ({ ...current, mode: event.target.value }))} value={flowForm.mode}><option>Sequential</option><option>Any-one approver</option><option>All required approvers</option></select></label><button className="primary-action" disabled={saving} type="submit">Create flow</button></form></section></div></ModalPortal> : null}
        <div className="functional-panel-header"><div><span>Approval flows</span><h3>Configured flows</h3></div><small>{flowRecords.length} active</small></div>
        <div className="leave-request-list">{flowRecords.map((flow) => <article key={flow.id}><div><strong>{flow.title}</strong><span>{flow.description}</span></div><em>{flow.status}</em></article>)}{!flowRecords.length ? <p className="empty-state">No approval flows configured.</p> : null}</div>
      </section>;
    }

    if (activeTab === "Attendance") {
      const myAttendance = attendance.find((item) => item.userId === currentUser?.id);
      const canConfigureAttendance = Boolean(currentUser?.canManagePeople || currentUser?.accessLevel === "Owner");
      return (
        <section className="functional-panel">
          <div className="functional-panel-header">
            <div>
              <span>Attendance</span>
              <h3>Daily availability</h3>
            </div>
            <div className="person-actions">
              <button disabled={saving || myAttendance?.status === "Present"} onClick={() => markAttendance("in")} type="button">Clock in</button>
              <button disabled={saving || !myAttendance?.clockInAt || Boolean(myAttendance?.clockOutAt)} onClick={() => markAttendance("out")} type="button">Clock out</button>
            </div>
          </div>
          {canConfigureAttendance ? <><button className="secondary-action" onClick={() => setAttendanceSettingsOpen(true)} type="button">Configure automatic attendance</button>{attendanceSettingsOpen ? <ModalPortal><div className="workspace-modal-backdrop" onMouseDown={() => setAttendanceSettingsOpen(false)} role="presentation"><section aria-modal="true" className="workspace-modal" onMouseDown={(event) => event.stopPropagation()} role="dialog"><header><div><span>People</span><h3>Automatic attendance</h3></div><button aria-label="Close" onClick={() => setAttendanceSettingsOpen(false)} type="button">×</button></header><div className="people-setup-form"><label><input checked={attendanceSettings.autoMarkEnabled} onChange={(event) => setAttendanceSettings((current) => ({ ...current, autoMarkEnabled: event.target.checked }))} type="checkbox" /> Mark attendance automatically after active Workspace use</label><p className="people-attendance-policy">Only time while the Workspace is open and visible on screen is counted. Hidden tabs and closed sessions do not add to attendance time.</p><label>Active screen time<select disabled={!attendanceSettings.autoMarkEnabled} onChange={(event) => setAttendanceSettings((current) => ({ ...current, autoMarkMinutes: Number(event.target.value) }))} value={attendanceSettings.autoMarkMinutes}><option value={30}>30 minutes</option><option value={60}>1 hour</option><option value={90}>1 hour 30 minutes</option><option value={120}>2 hours</option><option value={180}>3 hours</option><option value={240}>4 hours</option><option value={300}>5 hours</option><option value={360}>6 hours</option><option value={420}>7 hours</option><option value={480}>8 hours</option></select></label><button className="primary-action" disabled={saving} onClick={async () => { await saveAttendanceSettings(); setAttendanceSettingsOpen(false); }} type="button">Save attendance settings</button></div></section></div></ModalPortal> : null}</> : null}
          <div className="people-status-list">
            {attendance.map((item) => <article key={item.id}><div><strong>{item.personName || "Workspace member"}</strong><span>{item.source === "automatic" ? "Marked from active Workspace use" : "Marked manually"}</span></div><em>{item.status}</em><small>{item.clockInAt ? `In ${new Date(item.clockInAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "Not clocked in"}</small></article>)}
            {!attendance.length ? <p className="empty-state">No attendance has been marked today.</p> : null}
          </div>
        </section>
      );
    }

    if (activeTab === "Leaves") {
      return (
        <section className="functional-panel leave-management-panel">
          <div className="functional-panel-header">
            <div>
              <span>Leaves</span>
              <h3>Leave requests and availability</h3>
            </div>
            <button className="primary-action" onClick={() => setLeaveFormOpen(true)} type="button">Apply for leave</button>
          </div>
          {leaveFormOpen ? (
            <ModalPortal><div className="workspace-modal-backdrop" role="presentation">
              <section aria-labelledby="leave-modal-title" aria-modal="true" className="workspace-modal" role="dialog">
                <header><div><span>People</span><h3 id="leave-modal-title">{editingLeaveRequest ? "Edit leave request" : "Apply for leave"}</h3></div><button aria-label="Close" onClick={() => { setLeaveFormOpen(false); setEditingLeaveRequest(null); }} type="button">×</button></header>
                <form className="leave-request-form" onSubmit={submitLeave}>
              <label>Leave type<select value={leaveForm.type} onChange={(event) => setLeaveForm({ ...leaveForm, type: event.target.value })}><option>Casual Leave</option><option>Sick Leave</option><option>Paid Leave</option><option>Emergency Leave</option><option>Exam Leave</option><option>Work From Home</option></select></label>
              <label>From<input required type="date" value={leaveForm.start} onChange={(event) => setLeaveForm({ ...leaveForm, start: event.target.value })} /></label>
              <label>To<input min={leaveForm.start} required type="date" value={leaveForm.end} onChange={(event) => setLeaveForm({ ...leaveForm, end: event.target.value })} /></label>
              {leaveApprover ? <div className="leave-assigned-approver"><span>Assigned approver{leaveApprovers.length > 1 ? "s" : ""}</span><strong>{leaveApprover}</strong><small>Your request will be sent to the configured approver{leaveApprovers.length > 1 ? "s" : ""}.</small></div> : null}
              <label className="form-wide">Reason<textarea required rows="3" placeholder="Briefly explain the reason and any handover details" value={leaveForm.reason} onChange={(event) => setLeaveForm({ ...leaveForm, reason: event.target.value })} /></label>
              <button className="primary-action" disabled={saving} type="submit">{saving ? <WorkspaceInlineLoader compact label="Saving" /> : editingLeaveRequest ? "Save changes" : "Submit request"}</button>
                </form>
              </section>
            </div></ModalPortal>
          ) : null}
          <div className="leave-request-list">
            {leaveRequests.map((request) => <article key={request.id}><div><strong>{request.title}</strong><span>{request.description || request.due}</span></div><em>{request.status}</em>{["Pending", "Needs Review"].includes(request.status) && isOwnLeaveRequest(request) ? <div className="leave-row-actions"><button disabled={saving} onClick={() => openLeaveEditor(request)} type="button">Edit</button><button disabled={saving} onClick={() => onUpdateRecord?.(request.id, { status: "Cancelled" })} type="button">Cancel</button></div> : null}{["Pending", "Needs Review"].includes(request.status) && canApproveLeaveRequest(request) && !isOwnLeaveRequest(request) ? <button disabled={saving} onClick={() => { setSelectedLeaveRequest(request); setLeaveDecisionOpen(true); }} type="button">Review</button> : null}</article>)}
            {!leaveRequests.length ? <p className="empty-state">No leave requests yet.</p> : null}
          </div>
        </section>
      );
    }

    if (["Performance", "Assets", "Documents", "Payroll"].includes(activeTab)) {
      if (activeTab === "Performance") {
        const usage = activity.filter((item) => ["tracker_activity", "page_visit"].includes(item.eventType));
        return <section className="functional-panel"><div className="functional-panel-header"><div><span>Usage report</span><h3>Per-user tracked activity</h3></div><small>{usage.length} events</small></div><div className="people-data-table-wrap"><table className="people-data-table"><thead><tr><th>User</th><th>Area</th><th>Activity</th><th>Tracked time</th><th>When</th></tr></thead><tbody>{usage.map((item) => <tr key={item.id}><td>{item.userName}</td><td>{item.moduleName} · {item.tab}</td><td>{item.message}</td><td>{Math.floor((item.durationSeconds || 0) / 60)} min</td><td>{item.at}</td></tr>)}</tbody></table>{!usage.length ? <p className="empty-state">No tracked usage has synced yet.</p> : null}</div></section>;
      }
      return (
        <section className="functional-panel">
          <div className="functional-panel-header">
            <div>
              <span>{activeTab}</span>
              <h3>{activeTab} records</h3>
            </div>
            <small>{filteredPeople.length} people</small>
          </div>
          {renderOperationalCards(activeTab)}
        </section>
      );
    }

    return (
      <section className="functional-panel">
        <div className="functional-panel-header">
          <div>
            <span>{activeTab}</span>
            <h3>{activeTab === "Employees" ? "Employee directory" : "People directory"}</h3>
          </div>
          <div className="person-actions"><small>{filteredPeople.length} shown</small><button className="primary-action" onClick={() => openCreate()} type="button">Invite person</button></div>
        </div>
        {renderPersonRows()}
      </section>
    );
  };

  return (
    <>
      {setActiveTab ? <ModuleTabStrip moduleName="People" activeTab={activeTab} setActiveTab={setActiveTab} tabs={visibleTabs} /> : null}
      <section className="functional-hero people-hero">
        <div className="people-tab-intro"><p>{tabHero}</p><HelpTrigger title={`People · ${activeTab}`}><p>{tabHero}</p></HelpTrigger></div>
        {renderHeroAction()}
      </section>

      {activeTab === "Overview" ? (
        <>
          <section className="functional-metrics people-metrics" aria-label="People summary">
            {stats.map((item) => (
              <article key={item.label}>
                <span>{item.label}</span>
                <strong>{item.value}</strong>
                <small>{item.note}</small>
              </article>
            ))}
          </section>
          <p className="people-overview-note">Use the People directory to manage employee records and invitations. Departments, teams, access, and operational workflows are available in their dedicated tabs.</p>
        </>
      ) : null}

      {activeTab !== "Overview" ? <section className="functional-toolbar people-toolbar">
        <label>
          {searchCopy[0]}
          <input onChange={(event) => setQuery(event.target.value)} placeholder={searchCopy[1]} type="search" value={query} />
        </label>
        <button className="secondary-action" disabled={loading} onClick={loadPeople} type="button">
          <Refresh fontSize="small" />
          Refresh
        </button>
      </section> : null}

      {message ? <p className="workspace-dialog-success people-message">{message}</p> : null}
      {error && !formOpen ? <p className="people-error">{error}</p> : null}

      {renderDepartmentDialog()}
      {renderTeamDialog()}

      <PeopleDialog description={personDialogMode === "transfer" ? "Move this person to a new department, team, manager, designation, location, or project assignment." : form.id ? "Update employment, reporting, and Workspace access details." : "Invite a person into the workspace with the right employment context, access level, and module permissions."} id="person-modal-title" onClose={() => setFormOpen(false)} open={formOpen} title={personDialogMode === "transfer" ? "Transfer person" : form.id ? "Edit person" : "Invite person"}>
        <form className="people-editor" onSubmit={submitPerson}>
          {error ? <p className="people-error" role="alert">{error}</p> : null}
          {!form.id ? (
            <div className="people-invite-note">
              <strong>Invite delivery</strong>
              <p>Invitation emails expire after 48 hours. For security, the same person can receive another invite only after 5 minutes.</p>
            </div>
          ) : null}
          {!form.id ? <div className="people-search-box">
            <label>
              Search Lyriclious users
              <input
                onChange={(event) => {
                  setUserQuery(event.target.value);
                  setSelectedInviteUser(null);
                }}
                placeholder="Search existing user by email or name"
                type="search"
                value={userQuery}
              />
            </label>
            <p className="people-search-help">Search, then select a person below to prefill their available account details.</p>
            {userResults.length ? (
              <div className="people-search-results">
                {userResults.map((user) => (
                  <button key={user.id} onClick={() => selectUser(user)} type="button">
                    <em>Select</em>
                    <span><strong>{user.name || user.email}</strong><small>{user.email}</small></span>
                  </button>
                ))}
              </div>
            ) : null}
            {selectedInviteUser ? <p className="people-user-selected" role="status">Selected: {selectedInviteUser.name || selectedInviteUser.email}. Available details have been filled below.</p> : null}
          </div> : null}

          {personDialogMode === "transfer" ? <div className="people-invite-note"><strong>Transfer details</strong><p>Only organizational fields can be changed here. Employment type, role, access, and status remain unchanged.</p></div> : null}

          <div className="people-form-grid">
            <label>
              Name
              <input disabled={personDialogMode === "transfer"} required onChange={updateForm("name")} value={form.name} />
            </label>
            <label>
              Email
              <input disabled={personDialogMode === "transfer"} required onChange={updateForm("email")} type="email" value={form.email} />
            </label>
            <label>
              Employment type
              <select disabled={personDialogMode === "transfer"} onChange={updateForm("employmentType")} value={form.employmentType}>
                {(meta.employmentTypes || fallbackMeta.employmentTypes).map((item) => <option key={item}>{item}</option>)}
              </select>
            </label>
            <label>
              Access level
              <select disabled={personDialogMode === "transfer"} onChange={updateForm("accessLevel")} value={form.accessLevel}>
                {(meta.accessLevels || fallbackMeta.accessLevels).map((item) => <option key={item}>{item}</option>)}
              </select>
            </label>
            <label>
              Workspace role
              {roleTemplateRows.some((role) => role.name === form.workspaceRole) ? (
                <select disabled={personDialogMode === "transfer"} onChange={chooseRoleTemplate} value={form.workspaceRole}>
                  {roleTemplateRows.map((role) => (
                    <option key={role.id || role.name} value={role.name}>{role.name}</option>
                  ))}
                  <option value="">Custom role</option>
                </select>
              ) : (
                <div className="people-inline-add">
                  <input disabled={personDialogMode === "transfer"} onChange={updateForm("workspaceRole")} placeholder="Custom role" value={form.workspaceRole} />
                  <button disabled={personDialogMode === "transfer"} onClick={() => chooseRoleTemplate({ target: { value: roleTemplateRows[0]?.name || "Employee" } })} type="button">
                    Use preset
                  </button>
                </div>
              )}
            </label>
            <label>
              Status
              <select disabled={personDialogMode === "transfer"} onChange={updateForm("status")} value={form.status}>
                {(meta.statuses || fallbackMeta.statuses).map((item) => <option key={item}>{item}</option>)}
              </select>
            </label>
            <label>
              Department
              {customDepartmentOpen ? (
                <div className="people-inline-add">
                  <input autoFocus onChange={updateForm("department")} placeholder="New department name" value={form.department} />
                  <button onClick={() => setCustomDepartmentOpen(false)} type="button">Use list</button>
                </div>
              ) : (
                <select onChange={chooseDepartment} value={departmentOptions.includes(form.department) ? form.department : ""}>
                  <option value="">No department</option>
                  {departmentOptions.map((department) => (
                    <option key={department} value={department}>{department}</option>
                  ))}
                  <option value="__new__">+ Add new department</option>
                </select>
              )}
            </label>
            <label>
              Team
              {customTeamOpen ? (
                <div className="people-inline-add">
                  <input autoFocus onChange={updateForm("team")} placeholder="New team name" value={form.team} />
                  <button onClick={() => setCustomTeamOpen(false)} type="button">Use list</button>
                </div>
              ) : (
                <select onChange={chooseTeam} value={teamOptions.includes(form.team) ? form.team : ""}>
                  <option value="">No team</option>
                  {teamOptions.map((team) => (
                    <option key={team} value={team}>{team}</option>
                  ))}
                  <option value="__new__">+ Add new team</option>
                </select>
              )}
            </label>
            <label>
              Designation
              <input onChange={updateForm("designation")} value={form.designation} />
            </label>
            <label>
              Reporting manager
              <select
                onChange={(event) => {
                  const manager = managerOptions.find((person) => person.userId === event.target.value);
                  setForm((current) => ({
                    ...current,
                    managerName: manager?.name || "",
                    managerUser: manager?.userId || "",
                  }));
                }}
                value={managerOptions.find((person) => person.userId === form.managerUser || person.name === form.managerName)?.userId || (form.managerName ? "__unlinked__" : "")}
              >
                <option value="">No reporting manager</option>
                {form.managerName && !managerOptions.some((person) => person.userId === form.managerUser || person.name === form.managerName) ? <option disabled value="__unlinked__">{form.managerName} (not available in this project)</option> : null}
                {managerOptions.map((person) => <option key={person.id} value={person.userId}>{person.name}{person.designation ? ` · ${person.designation}` : ""}</option>)}
              </select>
              <small>{selectedProjectName ? `Only active people assigned to ${selectedProjectName} are shown.` : "Choose an active person from this workspace."}</small>
            </label>
            <label className="form-wide">Leave approvers<select multiple onChange={(event) => setForm((current) => ({ ...current, leaveApprovers: Array.from(event.target.selectedOptions, (option) => option.value) }))} value={form.leaveApprovers || []}>{managerOptions.map((person) => <option key={person.id} value={person.name}>{person.name}{person.designation ? ` · ${person.designation}` : ""}</option>)}</select><small>Choose one or more people who can approve this employee’s leave requests.</small></label>
            <label>
              Joining date
              <input disabled={personDialogMode === "transfer"} onChange={updateForm("joiningDate")} type="date" value={form.joiningDate || ""} />
            </label>
            <label>
              Phone
              <input disabled={personDialogMode === "transfer"} onChange={updateForm("phone")} value={form.phone} />
            </label>
            <label>
              Location
              <input onChange={updateForm("location")} value={form.location} />
            </label>
            <label className="people-notes-field">
              Notes
              <textarea onChange={updateForm("notes")} value={form.notes} />
            </label>
          </div>

          {!selectedProjectName ? <div className="people-module-access">
            <span>Project assignment</span>
            <div>
              {availableProjects.map((project) => (
                <button
                  className={form.projectRefs.includes(project.name) ? "active" : ""}
                  key={project.id || project.name}
                  onClick={() => toggleProjectRef(project.name)}
                  type="button"
                >
                  {project.name}
                </button>
              ))}
              {!availableProjects.length ? <small>No projects created yet.</small> : null}
            </div>
          </div> : null}

          {personDialogMode !== "transfer" ? <div className="people-module-access">
            <span>Module access</span>
            <div>
              {(meta.modules || fallbackMeta.modules).map((module) => (
                <button
                  className={form.moduleAccess.includes(module.key) ? "active" : ""}
                  key={module.key}
                  onClick={() => toggleModule(module.key)}
                  type="button"
                >
                  {module.label}
                </button>
              ))}
            </div>
          </div> : null}

          <div className="workspace-dialog-actions">
            <button disabled={saving} onClick={() => setFormOpen(false)} type="button">Cancel</button>
            <button disabled={saving || !form.name.trim() || !form.email.trim()} type="submit">
              {saving ? <WorkspaceInlineLoader compact label={personDialogMode === "transfer" || form.id ? "Saving" : "Sending invite"} /> : personDialogMode === "transfer" ? "Save transfer" : form.id ? "Save changes" : "Send 48-hour invite"}
            </button>
          </div>
        </form>
      </PeopleDialog>

      <PeopleDialog description="Confirm the decision before updating this leave request." id="leave-decision-modal-title" onClose={() => { setLeaveDecisionOpen(false); setSelectedLeaveRequest(null); }} open={leaveDecisionOpen} title="Review leave request">
        <div className="people-setup-form">
          <p><strong>{selectedLeaveRequest?.title}</strong></p>
          <p>{selectedLeaveRequest?.description || selectedLeaveRequest?.due || "No leave details provided."}</p>
          <div className="workspace-dialog-actions">
            <button disabled={saving} onClick={() => { setLeaveDecisionOpen(false); setSelectedLeaveRequest(null); }} type="button">Cancel</button>
            <button disabled={saving} onClick={() => decideLeaveRequest("Rejected")} type="button">Reject</button>
            <button className="primary-action" disabled={saving} onClick={() => decideLeaveRequest("Approved")} type="button">Approve</button>
          </div>
        </div>
      </PeopleDialog>

      {renderTabContent()}
    </>
  );
}
